import { Button } from "@/components/ui/button"
import { ArrowRight, Check, Star } from "lucide-react"
import { useState } from "react"
import { DemoBookingModal } from "./demo-booking-modal"
import { ResourceGateModal, checkResourceAccess } from "./resource-gate-modal"

const RESOURCE_URL = "https://www.npmjs.com/package/n8n-nodes-bridgegate"

const partners = ["American Lung Association", "Avesis", "HSN", "BlueStep", "CareOne", "Contexture", "KeyHIE", "QVC", "SunRx", "Therap", "WISHIN"]

export function HeroSection() {
  const [demoModalOpen, setDemoModalOpen] = useState(false)
  const [resourceModalOpen, setResourceModalOpen] = useState(false)

  // Check if user already has access before opening modal
  const handleResourceAccess = () => {
    if (checkResourceAccess()) {
      // User already has access, open resource directly
      window.open(RESOURCE_URL, '_blank')
    } else {
      // Show the gate modal
      setResourceModalOpen(true)
    }
  }

  return (
    <>
      <section className="relative bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              {/* G2 Badge */}
              <div className="flex items-center gap-2 mb-6">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#9e3667] text-[#9e3667]" />
                  ))}
                </div>
                <span className="text-sm text-[#6b6b7b]">4.9/5 on G2 — Trusted by 100+ enterprises</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-[#2d2d3a] leading-[1.1] mb-4">
                Stop Coding EMR Integrations.{" "}
                <span className="bg-[linear-gradient(135deg,#a63083,#bf406a)] bg-clip-text text-transparent">
                  Start Orchestrating Them.
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl text-[#2d2d3a] font-semibold mb-4">
                Connect any EMR, anytime.
              </p>

              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-[#faf5f7] text-[#9e3667] px-4 py-2 rounded-full text-sm font-medium mb-5">
                <Check className="h-4 w-4" />
                BridgeGate Node for n8n Now Available
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed mb-8 max-w-lg">
                If you've ever tried to pull data from Epic, Cerner, or Athena, you know the pain of custom API builds. 
                Now you can do it within n8n's low-code interface using the power of BridgeGate.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4">
                <Button 
                  size="lg"
                  onClick={() => setDemoModalOpen(true)}
                  className="bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:opacity-90 text-white font-medium px-6 h-11"
                >
                  Talk to an Expert
                </Button>
                <button 
                  onClick={handleResourceAccess}
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-xl border-2 border-[#9e3667] text-[#9e3667] font-semibold transition-all hover:bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:border-transparent hover:text-white"
                >
                  View Free N8N Node
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Right side - Illustration */}
            <div className="hidden lg:block relative">
              <div className="relative w-full max-w-md mx-auto">
                {/* Background shape */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#faf5f7] via-[#fef7f7] to-[#fffbf5] rounded-3xl -z-10" />
                
                {/* Workflow diagram */}
                <div className="p-8">
                  <div className="flex items-center justify-between gap-4">
                    {/* EMR Sources */}
                    <div className="space-y-3">
                      {["Epic", "Cerner", "Athena"].map((emr) => (
                        <div key={emr} className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200 text-center min-w-[80px]">
                          <span className="text-sm font-semibold text-[#2d2d3a]">{emr}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Arrows */}
                    <div className="flex flex-col items-center gap-2">
                      <ArrowRight className="h-5 w-5 text-[#9e3667]" />
                      <ArrowRight className="h-5 w-5 text-[#9e3667]" />
                      <ArrowRight className="h-5 w-5 text-[#9e3667]" />
                    </div>
                    
                    {/* BridgeGate */}
                    <div className="bg-[linear-gradient(135deg,#a63083,#bf406a)] rounded-2xl p-5 shadow-lg text-center">
                      <div className="text-white font-bold text-sm">BridgeGate</div>
                      <div className="text-white/80 text-xs mt-1">n8n Node</div>
                    </div>
                    
                    {/* Arrows */}
                    <div className="flex flex-col items-center gap-2">
                      <ArrowRight className="h-5 w-5 text-[#9e3667]" />
                      <ArrowRight className="h-5 w-5 text-[#9e3667]" />
                      <ArrowRight className="h-5 w-5 text-[#9e3667]" />
                    </div>
                    
                    {/* Outputs */}
                    <div className="space-y-3">
                      {["FHIR", "HL7", "JSON"].map((format) => (
                        <div key={format} className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200 text-center min-w-[70px]">
                          <span className="text-sm font-semibold text-[#2d2d3a]">{format}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted By Section */}
        <div className="border-t border-gray-100 bg-[#fafafa]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider text-center mb-6">
              Trusted by leading enterprises across healthcare, retail & finance
            </p>
            
            {/* Partner Logos Marquee */}
            <div className="relative overflow-hidden">
              <div className="flex animate-scroll">
                {[...Array(2)].map((_, setIndex) => (
                  <div key={setIndex} className="flex items-center gap-16 px-8">
                    {partners.map((partner) => (
                      <span key={`${setIndex}-${partner}`} className="flex-shrink-0 text-gray-400 font-semibold text-base whitespace-nowrap">
                        {partner}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <DemoBookingModal open={demoModalOpen} onOpenChange={setDemoModalOpen} />
      <ResourceGateModal 
        open={resourceModalOpen} 
        onOpenChange={setResourceModalOpen}
        resourceUrl={RESOURCE_URL}
        resourceName="n8n Node Documentation"
      />
    </>
  )
}
